import { ReactElement, useMemo, useState } from 'react';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { DashboardCard } from '@/components/DashboardCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { getUserFullName } from '@/components/UserList/utils';
import { PopupButton } from '@typeform/embed-react';
import useInvestor from '@/hooks/useInvestor';
import { Select } from 'react-daisyui';
import { Client } from '@types';

// eslint-disable-next-line import/prefer-default-export
export function InvestorAddUserCard(): JSX.Element | null {
  const { auth }: IUserContext = useUserContext(); // Use the context
  const { useStartups } = useInvestor();
  const { data: startups } = useStartups();
  const [company, setCompany] = useState<Client | null>(null);

  const { Option } = Select;

  // eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment
  const FORM_ID: string = (import.meta.env || process.env)
    ?.VITE_TYPEFORM_ADD_NEW_USER_INVESTOR;

  const getHiddenProps = useMemo<Record<string, string>>((): Record<
    string,
    string
  > => {
    const userFullName = auth?.id ? getUserFullName(auth) : '';
    const addedById = auth?.id.toString() || '';
    const companyId = company?.hubspot_id || '';
    const companyName = company?.client_name || '';

    return {
      company_name: companyName,
      added_by_full_name: userFullName,
      added_by_id: addedById,
      company_id: companyId,
    };
  }, [auth, company]);

  return (
    <DashboardCard data-testid='investor-add-user-card'>
      <div className='flex flex-col md:flex-row'>
        <FontAwesomeIcon
          icon={faUserPlus}
          size='3x'
          className='text-violet-600 mr-2 py-3'
        />
        <div className='text-base grow py-3 min-w-[270px]'>
          <div className='font-bold text-base'>Add a user for a company</div>
          <div className='text-base'>
            <Select
              defaultValue='default'
              className='select select-bordered select-accent w-full mt-4'
              onChange={(selectedCompanyId: number) => {
                setCompany(
                  startups?.find(
                    (startup) => startup.id === Number(selectedCompanyId)
                  ) as Client
                );
              }}
            >
              <Option value='default' disabled>
                Select a company
              </Option>

              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */}
              {
                (startups as Client[])?.map((client: Client) => (
                  <Option key={String(client.id)} value={String(client.id)}>
                    {client.client_name}
                  </Option>
                )) as unknown as ReactElement
              }
            </Select>
          </div>
          {company?.id && (
            <div className='mt-2'>
              <PopupButton
                id={FORM_ID}
                hidden={getHiddenProps}
                transitiveSearchParams={['']}
                className='btn btn-accent btn-sm my-2'
              >
                Add User
              </PopupButton>
            </div>
          )}
        </div>
      </div>
    </DashboardCard>
  );
}
